import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as IoIcons from 'react-icons/io5';
import * as HiIcons from 'react-icons/hi';
import * as MdIcons from 'react-icons/md';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiCheck, FiMail, FiShield, FiUsers, FiTrendingUp, FiSettings, FiVideo, FiZap, FiBarChart3, FiMessageCircle, FiArrowRight, FiClock, FiStar, FiAward, FiCheckCircle, FiPackage } = FiIcons;
const { IoLogoWhatsapp, IoCheckmarkCircle } = IoIcons;
const { HiSparkles, HiLightningBolt } = HiIcons;
const { MdSecurity, MdVerified, MdDiamond } = MdIcons;

function App() {
  const [progress, setProgress] = useState(0);
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prev + 2;
      });
    }, 100);

    return () => clearInterval(timer);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const features = [
    {
      icon: FiZap,
      title: "Unlimited Webinars",
      description: "Host as many events as you want"
    },
    {
      icon: FiSettings,
      title: "AI Webinar Automation",
      description: "Like-live, evergreen, timed chat"
    },
    {
      icon: FiVideo,
      title: "HD Streaming",
      description: "Professional, high-quality video"
    },
    {
      icon: FiTrendingUp,
      title: "Full Funnel Builder",
      description: "Registration, Thank You, Watch Room"
    },
    {
      icon: FiMessageCircle,
      title: "Live Chat & Q&A Tools",
      description: "Boost engagement"
    },
    {
      icon: FiBarChart3,
      title: "Analytics Dashboard",
      description: "Track attendance & conversions"
    }
  ];

  const trustBadges = [
    { icon: MdSecurity, text: "Secure Payments" },
    { icon: FiShield, text: "7-Day Refund Guarantee" },
    { icon: FiUsers, text: "Trusted by 1000+ Coaches" },
    { icon: MdVerified, text: "GDPR Safe" },
    { icon: FiClock, text: "Fast Support" }
  ];

  // Smooth scroll function
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#023e8a] via-[#0077b6] to-[#023e8a] text-white overflow-hidden relative">
      {/* Animated Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-white/5 rounded-full blur-[100px] animate-pulse-slow"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-[#00b4d8]/10 rounded-full blur-[100px] animate-pulse-slow delay-1000"></div>
      </div>

      {/* WhatsApp Sticky Button */}
      <motion.a
        href="https://wa.me/919884899080"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110"
        whileHover={{ scale: 1.1, rotate: 10 }}
        whileTap={{ scale: 0.95 }}
      >
        <SafeIcon icon={IoLogoWhatsapp} className="text-3xl" />
      </motion.a>

      {/* Hero Section */}
      <motion.section 
        id="home"
        className="container mx-auto px-6 pt-20 pb-12 text-center relative z-10"
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full mb-6 shadow-[0_0_30px_rgba(255,255,255,0.3)] animate-float">
            <SafeIcon icon={IoCheckmarkCircle} className="text-6xl text-[#023e8a]" />
          </div>
          
          <h1 className="text-5xl md:text-7xl font-extrabold mb-6 drop-shadow-lg leading-tight tracking-tight font-playfair">
            You're In! <br/>
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-[#caf0f8] to-white">
              Subscription Activated
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto font-light tracking-wide">
            Thank you for grabbing the Black Friday Deal — your account is being set up.
          </p>

          <motion.div 
            className="inline-block bg-white/10 backdrop-blur-md p-1 rounded-full shadow-xl mb-8 border border-white/20"
            whileHover={{ scale: 1.05 }}
          >
            <div className="px-8 py-3 rounded-full flex items-center justify-center space-x-3">
              <SafeIcon icon={MdDiamond} className="text-[#ffd700] text-xl" />
              <span className="text-lg font-semibold text-white">Yearly Plan – ₹7,997 Paid Successfully</span>
              <SafeIcon icon={MdDiamond} className="text-[#ffd700] text-xl" />
            </div>
          </motion.div>
        </motion.div>
      </motion.section>

      {/* Account Setup Instructions */}
      <motion.section 
        id="what-happens-next"
        className="container mx-auto px-6 py-8 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="bg-white text-[#023e8a] rounded-3xl p-8 shadow-[0_20px_50px_rgba(0,0,0,0.3)] border border-white/50 relative overflow-hidden"
            whileHover={{ y: -5 }}
            transition={{ duration: 0.3 }}
          >
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-16 -mt-16 opacity-50 pointer-events-none"></div>
            
            <h2 className="text-3xl font-bold mb-8 text-center font-playfair">
              What Happens Next?
            </h2>
            
            <div className="space-y-4 relative z-10">
              {[
                { icon: FiShield, text: "Your account is being activated automatically." },
                { icon: FiMail, text: "Your login details will arrive in your email within 2–5 minutes." },
                { icon: FiZap, text: "You can start creating webinars immediately after logging in." },
                { icon: FiAward, text: "Your Yearly Plan includes unlimited webinars, HD streaming & AI tools." },
                { icon: IoLogoWhatsapp, text: "Support is available on WhatsApp anytime." }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  className="flex items-center space-x-4 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors duration-300 border border-blue-100"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.5 }}
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-[#023e8a] rounded-full flex items-center justify-center shadow-md">
                    <SafeIcon icon={item.icon} className="text-white text-lg" />
                  </div>
                  <span className="text-[#023e8a] font-medium text-lg">{item.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mt-10 relative z-10">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-500">Account Provisioning Status</span>
                <span className="text-sm font-bold text-[#0077b6]">{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
                <motion.div 
                  className="bg-gradient-to-r from-[#0077b6] to-[#00b4d8] h-4 rounded-full shadow-lg relative"
                  style={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                >
                   <div className="absolute top-0 left-0 h-full w-full bg-white/30 animate-shimmer"></div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Features Grid */}
      <motion.section 
        id="key-features"
        className="container mx-auto px-6 py-16 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12 text-white drop-shadow-md font-playfair">
            Key Features You Now Have Access To
          </h2>
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-white/10 hover:bg-white/20 transition-all duration-300 group"
                variants={fadeInUp}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={feature.icon} className="text-[#023e8a] text-2xl" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{feature.title}</h3>
                <p className="text-blue-100/80 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Upgrade Section - Direct to Lifetime */}
      <motion.section 
        id="see-upgrade-option"
        className="container mx-auto px-6 py-12 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="bg-gradient-to-r from-white to-blue-50 rounded-3xl p-10 text-center shadow-[0_20px_60px_rgba(0,0,0,0.4)] border-4 border-white/20 relative overflow-hidden"
            whileHover={{ scale: 1.01 }}
          >
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-[#FFD700] to-[#FFA500]"></div>
            
            <SafeIcon icon={HiSparkles} className="text-[#FFA500] text-5xl mx-auto mb-4 animate-pulse" />
            <h2 className="text-4xl font-bold mb-4 text-[#023e8a] font-playfair">
              Want Lifetime Access & Unlimited Support?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Upgrade to Lifetime for one-time payment and never worry about renewals again.
            </p>
            
            <motion.a
              href="https://paymentz.aifunnels.app/product/21362/25177?staffId=0&ac=1"
              target="_blank"
              className="inline-block bg-gradient-to-r from-[#FFA500] to-[#FF8C00] text-white px-10 py-5 rounded-full font-bold text-xl shadow-lg hover:shadow-2xl hover:translate-y-[-2px] transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Upgrade to Lifetime Access
            </motion.a>
          </motion.div>
        </div>
      </motion.section>

      {/* Done For You Offer - NEW SECTION */}
      <motion.section 
        id="done-for-you-setup"
        className="container mx-auto px-6 py-12 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="bg-[#0b1120] rounded-3xl p-1 overflow-hidden shadow-2xl"
            whileHover={{ scale: 1.01 }}
          >
            <div className="bg-[#0b1120] rounded-[22px] p-8 md:p-12 border border-gray-800 relative overflow-hidden">
              {/* Background glow */}
              <div className="absolute top-0 right-0 w-[300px] h-[300px] bg-[#FFA500]/20 rounded-full blur-[80px] pointer-events-none"></div>
              
              <div className="flex flex-col md:flex-row items-center gap-8 relative z-10">
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-block bg-[#FFA500] text-black font-bold text-xs px-3 py-1 rounded-full mb-4 uppercase tracking-wider">
                    Exclusive Black Friday Offer
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight overflow-visible whitespace-normal break-words">
                    Done For You Setup
                  </h2>
                  <p className="text-gray-400 text-lg mb-6">
                    Skip the learning curve. Let our expert team build your entire automated webinar funnel, configured perfectly for your business.
                  </p>
                  <ul className="text-gray-300 mb-8 space-y-2 text-left inline-block">
                    <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-[#FFA500]" /> Complete Funnel Configuration</li>
                    <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-[#FFA500]" /> Email Sequence Integration</li>
                    <li className="flex items-center gap-2"><SafeIcon icon={FiCheck} className="text-[#FFA500]" /> Payment Gateway Setup</li>
                  </ul>
                </div>
                
                <div className="flex-shrink-0">
                   <motion.a 
                    href="https://paymentz.aifunnels.app/product/21362/25737?staffId=0&ac=1"
                    target="_blank"
                    className="block w-full md:w-auto bg-[#FFA500] hover:bg-[#FF8C00] text-black px-10 py-6 rounded-2xl font-black text-xl shadow-[0_0_30px_rgba(255,165,0,0.4)] hover:shadow-[0_0_50px_rgba(255,165,0,0.6)] transition-all duration-300 text-center"
                    whileHover={{ scale: 1.05, rotate: 1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Get DFY Setup <br/>
                    <span className="text-sm font-medium opacity-80">Limited Spots Available</span>
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Support Section */}
      <motion.section 
        className="container mx-auto px-6 py-12 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8 text-white font-playfair">
            We're Here to Help You 24/7
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <motion.a
              href="https://wa.me/919884899080"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#023e8a] px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-blue-50 transition-all duration-300 flex items-center justify-center space-x-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={IoLogoWhatsapp} className="text-2xl text-[#25D366]" />
              <span>WhatsApp Support</span>
            </motion.a>
            
            <motion.a
              href="mailto:support@yinguru.com"
              className="bg-[#023e8a]/50 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-[#023e8a] transition-all duration-300 flex items-center justify-center space-x-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiMail} className="text-2xl" />
              <span>Email Support</span>
            </motion.a>
          </div>
        </div>
      </motion.section>

      {/* Trust Badges */}
      <motion.section 
        className="container mx-auto px-6 py-12 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-5 gap-6"
            variants={staggerContainer}
          >
            {trustBadges.map((badge, index) => (
              <motion.div 
                key={index}
                className="bg-white/5 backdrop-blur-sm rounded-xl p-4 text-center border border-white/10 hover:bg-white/10 transition-all duration-300"
                variants={fadeInUp}
                whileHover={{ y: -3 }}
              >
                <SafeIcon icon={badge.icon} className="text-[#FFA500] text-2xl mx-auto mb-2" />
                <p className="text-white text-xs font-semibold uppercase tracking-wide">{badge.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* CTA Section - Create Your First Webinar */}
      <motion.section 
        id="create-your-first-webinar"
        className="container mx-auto px-6 py-16 relative z-10"
        variants={fadeInUp}
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            className="bg-gradient-to-br from-white to-blue-50 rounded-[3rem] p-12 shadow-2xl border-4 border-white/30"
            whileHover={{ scale: 1.02 }}
          >
            <h2 className="text-4xl font-bold mb-6 text-[#023e8a] font-playfair">
              Create Your First Webinar
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              This is the perfect time to launch your automated or live session.
            </p>
            
            <motion.a
              href="https://wa.me/919884899080"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#023e8a] text-white px-12 py-6 rounded-2xl font-bold text-xl shadow-xl hover:shadow-2xl hover:bg-[#00b4d8] transition-all duration-300 flex items-center justify-center space-x-3 mx-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={IoLogoWhatsapp} className="text-2xl" />
              <span>Start on WhatsApp</span>
            </motion.a>
          </motion.div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="bg-[#001233] border-t border-white/10 py-12 relative z-10 text-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-[#FFA500] font-bold text-lg mb-4 uppercase tracking-wider">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('what-happens-next')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">What Happens Next?</button></li>
                <li><button onClick={() => scrollToSection('key-features')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Key Features</button></li>
                <li><button onClick={() => scrollToSection('see-upgrade-option')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">See Upgrade Option</button></li>
                <li><button onClick={() => scrollToSection('done-for-you-setup')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Done For You Setup</button></li>
                <li><button onClick={() => scrollToSection('create-your-first-webinar')} className="text-gray-400 hover:text-white transition-colors cursor-pointer">Create Your First Webinar</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-[#FFA500] font-bold text-lg mb-4 uppercase tracking-wider">Support</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="mailto:support@yinguru.com" className="text-gray-400 hover:text-white transition-colors">Email: support@yinguru.com</a></li>
                <li><a href="https://wa.me/919884899080" className="text-gray-400 hover:text-white transition-colors">WhatsApp Support</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-gray-400 text-sm mb-4">
              © 2025 My Webinar. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs max-w-4xl mx-auto leading-relaxed">
              This site is not part of the Facebook website or Facebook Inc. Additionally, this site is NOT endorsed by Facebook. FACEBOOK is a trademark of FACEBOOK, Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;